import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="confirm-modal"
export default class extends Controller {
  // Targets for the modal itself and the elements to update
  static targets = [
    "modal",
    "form", // Existing targets
    "title",
    "message",
    "irreversibleMessage", // Text elements
    "confirmButton",
    "cancelButton" // Buttons (to update text if needed)
  ];

  // Values to configure the modal content and behavior
  static values = {
    title: String,
    message: String,
    irreversibleMessage: String,
    confirmButtonText: String,
    cancelButtonText: String
    // Optional: You might want to pass the form selector or URL if it's not always the same target
    // formSelector: String
  };

  connect() {
    // Optional: Add keydown listener for Escape key
    // this.keydownHandler = this.handleKeydown.bind(this)
    // document.addEventListener("keydown", this.keydownHandler)

    // Ensure modal is hidden on initial load, although CSS should handle this
    this.close();
  }

  disconnect() {
    // Optional: Remove keydown listener
    // document.removeEventListener("keydown", this.keydownHandler)
  }

  open(event) {
    event.preventDefault();

    // If the trigger element passed specific values, use them
    // This allows overriding defaults per button
    const triggerElement = event.currentTarget;
    this.updateContent(triggerElement.dataset);

    this.modalTarget.classList.remove("hidden");
    document.body.classList.add("overflow-hidden");

    // Optional: Add classes for entry animation if using transitions
    // Example using Tailwind CSS transitions:
    // this.modalTarget.classList.remove("opacity-0")
    // this.modalTarget.classList.add("ease-out", "duration-300", "opacity-100")
    // this.modalTarget.querySelector('div[aria-hidden="true"]').classList.remove("opacity-0") // Background overlay
    // this.modalTarget.querySelector('div[aria-hidden="true"]').classList.add("ease-out", "duration-300", "opacity-100")
    // this.modalTarget.querySelector('.inline-block').classList.remove("opacity-0", "translate-y-4", "sm:translate-y-0", "sm:scale-95") // Modal panel
    // this.modalTarget.querySelector('.inline-block').classList.add("ease-out", "duration-300", "opacity-100", "translate-y-0", "sm:scale-100")
  }

  // New method to populate modal content
  updateContent(dataset) {
    // Use provided dataset values or fall back to controller's static values
    if (this.hasTitleTarget) {
      this.titleTarget.textContent =
        dataset.confirmModalTitleValue || this.titleValue;
    }
    if (this.hasMessageTarget) {
      this.messageTarget.textContent =
        dataset.confirmModalMessageValue || this.messageValue;
    }
    if (this.hasIrreversibleMessageTarget) {
      const irreversibleMsg =
        dataset.confirmModalIrreversibleMessageValue ||
        this.irreversibleMessageValue;
      this.irreversibleMessageTarget.textContent = irreversibleMsg;
      // Hide the element if the message is empty
      this.irreversibleMessageTarget.classList.toggle(
        "hidden",
        !irreversibleMsg
      );
    }
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.textContent =
        dataset.confirmModalConfirmButtonTextValue ||
        this.confirmButtonTextValue;
    }
    if (this.hasCancelButtonTarget) {
      this.cancelButtonTarget.textContent =
        dataset.confirmModalCancelButtonTextValue || this.cancelButtonTextValue;
    }

    // If a specific form selector is passed via trigger, update the form target
    // if (dataset.confirmModalFormSelectorValue && document.querySelector(dataset.confirmModalFormSelectorValue)) {
    //   this.formTarget = document.querySelector(dataset.confirmModalFormSelectorValue)
    // } else if (!this.hasFormTarget && this.element.querySelector('form')) {
    //   // Fallback to finding the first form within the controller element if no formTarget was set initially
    //   this.formTarget = this.element.querySelector('form')
    // }
  }

  close() {
    // Hide the modal
    this.modalTarget.classList.add("hidden");
    // Remove body class to restore scrolling
    document.body.classList.remove("overflow-hidden");

    // Optional: Add classes for exit animation if using transitions
    // Example using Tailwind CSS transitions:
    // this.modalTarget.classList.remove("ease-out", "duration-300", "opacity-100")
    // this.modalTarget.classList.add("ease-in", "duration-200", "opacity-0")
    // this.modalTarget.querySelector('div[aria-hidden="true"]').classList.remove("ease-out", "duration-300", "opacity-100") // Background overlay
    // this.modalTarget.querySelector('div[aria-hidden="true"]').classList.add("ease-in", "duration-200", "opacity-0")
    // this.modalTarget.querySelector('.inline-block').classList.remove("ease-out", "duration-300", "opacity-100", "translate-y-0", "sm:scale-100") // Modal panel
    // this.modalTarget.querySelector('.inline-block').classList.add("ease-in", "duration-200", "opacity-0", "translate-y-4", "sm:translate-y-0", "sm:scale-95")
  }

  confirm(event) {
    event.preventDefault();
    // Ensure we have a form target before submitting
    if (this.hasFormTarget) {
      this.formTarget.requestSubmit();
    } else {
      console.error("Confirm modal: Form target not found.");
    }
    this.close();
  }

  // Allows closing the modal by clicking the background overlay
  // Action defined in HTML: data-action="click->confirm-modal#close"

  // Allows closing the modal by pressing the Escape key
  // Action defined in HTML: data-action="keydown@window->confirm-modal#handleKeydown"
  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close();
    }
  }
}
